import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { DataZoomComponentOption, EChartsOption, LineSeriesOption, SeriesOption, YAXisComponentOption } from 'echarts';

// Enums
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';

// Utils
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import { generateTimeAxisValues } from '../../utils/chart-utils';
import { getDefaultDataZoom } from './helpers';
import { Coordinate, CycleComponents, DataEvent, LiveChartProps, ParameterGetOptionFn } from './types';
import {
  CHARACTER_SEPARATOR_DEVICE_NAME,
  CHARACTER_SEPARATOR_IOT_KEY_NAME,
  CHART_INDENTS,
  CLASS_NAME_SPACE,
  DATA_ZOOM_ID,
  DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS,
  DEVICE_NAME,
  SET_POINT_NAME,
  NOTE_ID,
  AXIS_CONFIG,
} from './constants';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from '../../utils/date-formatter';
import { DateFormats } from '../../enums/date-formats';
import ListLegendsCycle from '../ListLegendsCycle/ListLegendsCycle';
import getColorByDeviceIndex from '../../utils/getColorByDeviceIndex';
import applyYAxisPadding from '../../utils/applyYAxisPadding';

export const LiveChart: React.FC<LiveChartProps> = ({
  cycleComponents,
  createNote,
  editNote,
  measurement,
  isNoteView,
  chartPreference,
  cycleControllersName,
  nameDevices,
  chartRef,
  excludedChannelNames,
  cycleControllers,
  colorsDevices,
  generateNoteName,
  dateRange,
  dateToTimestamp,
}) => {
  const { t } = useTranslation();

  const isLoading = useMemo(() => {
    return !(cycleComponents && cycleComponents.source.length > 0);
  }, [cycleComponents]);

  const [chartReadyCounter, setChartReadyCounter] = useState(0);
  const onChartReady = () => {
    setChartReadyCounter((prev) => prev + 1);
  };

  const channelsToHide = excludedChannelNames?.flatMap((channelNames) => {
    const controllerName =
      cycleControllers?.find((item) => item.controller.id === channelNames.controllerId)?.controller.serial_number ||
      null;
    if (!controllerName) return [];

    const channelList = [...channelNames.excludeChannels, ...channelNames.excludeThermocouples];
    return channelList.map((channel) => `${controllerName}#${channel}`);
  });
  const defaultYAxisZoomSliderMargin: number = getTempByMeasurement(
    measurement,
    DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS,
  );

  const dataEvent = useRef<DataEvent | null>({});

  const getSerIdWithColor = (): { name: string; color: string }[] => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return [];

    const series = chartInstance.getOption().series as SeriesOption[];

    return series.reduce((acc, ser) => {
      const isNote = String(ser.name).startsWith(NOTE_ID);

      if (ser.id === SET_POINT_NAME || !ser.color || isNote) return acc;

      const name = ser.id as string;
      const color = (Array.isArray(ser.color) ? ser.color[0] : ser.color) as string;

      acc.push({ name, color });
      return acc;
    }, []);
  };

  /*========================Options===============================*/
  const generateTemperatureArray = (convertedMinTemp, convertedMaxTemp, temperature_grid_size, currentDataZoom) => {
    const result = [];

    const yZoomData = Array.isArray(currentDataZoom)
      ? currentDataZoom?.find((item) => item.id === DATA_ZOOM_ID.SLIDER_Y)
      : null;

    const minTemp = (yZoomData && yZoomData.startValue) || convertedMinTemp;
    const maxTemp = (yZoomData && yZoomData.endValue) || convertedMaxTemp;

    result.push(Math.ceil(minTemp));

    let start = Math.ceil(minTemp / temperature_grid_size) * temperature_grid_size;
    if (start < minTemp) start += temperature_grid_size;

    for (let temp = start; temp <= maxTemp; temp += temperature_grid_size) {
      result.push(temp || 0);
    }

    if (result[result.length - 1] !== maxTemp) {
      result.push(Math.floor(maxTemp));
    }

    return result;
  };

  const getOptions = ({
    deviceSeriesData,
    noteSeriesData,
    currentDataZoom,
    dateRange,
    dateTo,
    source,
  }: ParameterGetOptionFn & { dateRange: number; dateTo: number }): EChartsOption => {
    const cycleMeasurementSymbol =
      measurement === CycleMeasurement.Celsius ? CycleMeasurementSymbol.Celsius : CycleMeasurementSymbol.Fahrenheit;

    const minDate = dateTo - dateRange;
    const maxDate = dateTo;

    const minTemp = chartPreference.min_temperature;
    const maxTemp = chartPreference.max_temperature;

    const convertedMinTemp = getTempByMeasurement(measurement, minTemp);
    const convertedMaxTemp = getTempByMeasurement(measurement, maxTemp);

    const adjustedStartZoomY = convertedMinTemp - defaultYAxisZoomSliderMargin;
    const adjustedEndZoomY = convertedMaxTemp + defaultYAxisZoomSliderMargin;

    const clampedStartZoomY = adjustedStartZoomY < convertedMinTemp ? convertedMinTemp : adjustedStartZoomY;
    const clampedEndZoomY = adjustedEndZoomY > convertedMaxTemp ? convertedMaxTemp : adjustedEndZoomY;

    const dataZoom = currentDataZoom ? currentDataZoom : getDefaultDataZoom(clampedStartZoomY, clampedEndZoomY);

    const visibleDataSource = source;

    const deviceSeriesOptions: Record<string, LineSeriesOption> = {};
    const spSeriesOptions: SeriesOption = {
      name: SET_POINT_NAME,
      id: SET_POINT_NAME,
      type: 'line',
      symbol: 'none',
      showAllSymbol: false,
      showSymbol: false,
      clip: true,
      connectNulls: true,
      data: [],
      label: { show: false },
      endLabel: { show: false },
      labelLine: { show: false },
      color: '#0000FF',
      lineStyle: {
        color: '#0000FF',
        type: 'dashed',
      },
      emphasis: { disabled: true },
      select: { disabled: true },
      animation: false,
      silent: true,
    };

    for (const d of visibleDataSource) {
      const time = d.time;
      if (d.sp !== undefined) {
        const spItem: [number, number] = [time, d.sp];
        spSeriesOptions.data.push(spItem);
      }
      for (let i = 0; i < deviceSeriesData.length; i++) {
        const index = i;
        const item = deviceSeriesData[index];

        const temp = d[item.id];

        if (!temp) {
          continue;
        }
        const [controllerSerialNumber, device] = String(item.id).split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
        let customizedColor: string | undefined;

        if (colorsDevices?.length) {
          const match = colorsDevices.find((cd) => cd.controller_serial_number === controllerSerialNumber);
          if (match && (match.channelColors.length > 0 || match.thermocoupleColors.length > 0)) {
            const allColors = [...match.channelColors, ...match.thermocoupleColors];
            const deviceColorEntry = allColors.find((entry) => Object.keys(entry)[0] === device);
            customizedColor = deviceColorEntry ? Object.values(deviceColorEntry)[0] : undefined;
          }
        }

        const color = customizedColor ?? getColorByDeviceIndex(index, chartPreference.colors);

        const deviceItem: [number, number] = [time, temp];

        const option: SeriesOption = {
          name: item.name,
          id: item.id,
          type: 'line',
          symbol: 'none',
          showAllSymbol: false,
          showSymbol: false,
          clip: true,
          connectNulls: true,
          color,
          data: [] as [number, number][],
          xAxisIndex: 1,
          label: { show: false },
          endLabel: { show: false },
          labelLine: { show: false },
          lineStyle: { color },
          emphasis: { disabled: true },
          select: { disabled: true },
          animation: false,
          silent: true,
        };

        const isHide = channelsToHide.includes(String(item.name));
        if (!isHide) {
          if (!deviceSeriesOptions[item.id]) {
            deviceSeriesOptions[item.id] = option;
          }

          deviceSeriesOptions[item.id].data.push(deviceItem);
        }
      }
    }

    const noteSeries: LineSeriesOption[] = noteSeriesData.map((item, index) => {
      const defaultLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}`,
        fontSize: 14,
        borderWidth: 1,
        width: 48,
        lineHeight: 16,
        padding: [0, 4],
        align: 'center' as const,
        borderRadius: 0,
        backgroundColor: 'transparent',
        overflow: 'truncate' as const,
      };

      const noteViewLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}\n${item.name}`,
        fontSize: 16,
        color: '#000',
        borderColor: '#000',
        borderType: 'solid' as const,
        borderWidth: 1,
        width: 110,
        lineHeight: 24,
        padding: 20,
        borderRadius: 6,
        backgroundColor: 'white',
        overflow: 'truncate' as const,
      };

      const label = isNoteView ? noteViewLabel : defaultLabel;

      const markPointData = [
        {
          name: 'coordinate',
          occurrenceId: 'root',
          coord: item.media?.coord,
          label,
        },
        ...(item.links ?? []).map((link) => ({
          name: 'coordinate',
          occurrenceId: link.id,
          coord: link.coord,
          label,
        })),
      ];

      const noteSerOption: SeriesOption = {
        id: item.id,
        name: generateNoteName(item.name),
        encode: { x: 'time', y: item.id },
        type: 'line',
        connectNulls: false,
        clip: true,
        tooltip: {
          show: false,
        },
        symbol: 'none',
        lineStyle: {
          opacity: 0,
        },
        markPoint: {
          symbol: 'diamond',
          symbolSize: 12,
          itemStyle: {
            color: '#EF8541',
          },
          data: markPointData,
        },
      };

      return noteSerOption;
    });

    const series: LineSeriesOption[] = Object.values(deviceSeriesOptions);
    series.push(...noteSeries, spSeriesOptions);

    const customValues = generateTemperatureArray(
      convertedMinTemp,
      convertedMaxTemp,
      chartPreference.temperature_grid_size,
      currentDataZoom,
    );

    const yAxis: YAXisComponentOption = {
      type: 'value',
      scale: true,
      min: convertedMinTemp,
      max: convertedMaxTemp,
      interval: chartPreference.temperature_grid_size,
      splitLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisLabel: {
        formatter: (value) => `${value} ${cycleMeasurementSymbol}`,
        color: '#000',
        customValues,
      },
      axisTick: {
        show: true,
        customValues,
      },
      triggerEvent: false,
    };

    if (chartPreference.isDefaultTemplate) {
      yAxis.min = (value) => applyYAxisPadding(value.min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
      yAxis.max = (value) => applyYAxisPadding(value.max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

      yAxis.axisLabel = {
        ...yAxis.axisLabel,
        formatter: (value) => `${value}`,
      };

      delete yAxis.interval;
    }

    const chartOptions: EChartsOption = {
      tooltip: [
        {
          id: 'one',
          trigger: 'axis',
          position(point, _, __, ___, size) {
            const { contentSize, viewSize } = size;
            const [x, y] = point;
            const [contentWidth, contentHeight] = contentSize;
            const [viewWidth, viewHeight] = viewSize;

            const moveLeft = x + contentWidth >= viewWidth;
            const moveUp = y + contentHeight >= viewHeight;

            const adjustedX = moveLeft ? x - contentWidth : x;
            const adjustedY = moveUp ? y - contentHeight : y;

            return [adjustedX, adjustedY];
          },
          formatter: function (params) {
            const date = DateFormatter({
              date: new Date(params[0].axisValueLabel),
              format: DateFormats['dd-MMM yyyy HH:mm:ss a'],
            });
            let tooltipContent = `${date}<br/>`;

            params.forEach((item) => {
              const seriesName = item.seriesName;
              const value = item.data.at(1);
              const data = value ? `${value} ${cycleMeasurementSymbol}` : '';

              if (seriesName === SET_POINT_NAME) {
                tooltipContent += `${item.marker} ${SET_POINT_NAME} (${t('iot.setpoint')}): ${data}<br/>`;
                return;
              }

              const seriesEntity = item.seriesName.split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
              if (seriesEntity.length < 2) {
                tooltipContent += `${item.marker} ${seriesName}: ${data}<br/>`;
                return;
              }
              const controllerSerialNumber = seriesEntity[0];
              const deviceNameData = seriesEntity[1].split(CHARACTER_SEPARATOR_DEVICE_NAME);
              const deviceType = deviceNameData[0];
              const deviceNumber = deviceNameData[1];
              const formattedControllerName = cycleControllersName[controllerSerialNumber];
              const formattedDevice = DEVICE_NAME[deviceType] + CHARACTER_SEPARATOR_DEVICE_NAME + deviceNumber;

              const finishedDeviceName = nameDevices
                ? nameDevices[controllerSerialNumber + CHARACTER_SEPARATOR_IOT_KEY_NAME + seriesEntity[1]] ||
                formattedDevice
                : formattedDevice;
              tooltipContent += `${item.marker} ${finishedDeviceName}\t(${formattedControllerName}): ${data} <br/>`;
            });

            return tooltipContent;
          },
        },
      ],
      legend: {
        show: false,
      },
      grid: {
        show: true,
        top: `${CHART_INDENTS.GRID.TOP}px`,
        left: `${CHART_INDENTS.GRID.LEFT}px`,
        right: `${CHART_INDENTS.GRID.RIGHT}px`,
        bottom: `${CHART_INDENTS.GRID.BOTTOM}px`,
        borderColor: '#000',
      },
      xAxis: [
        {
          type: 'time',
          min: minDate,
          max: maxDate,
          position: 'bottom',
          axisTick: {
            customValues: generateTimeAxisValues(minDate, maxDate, chartPreference.time_grid_side),
          },
          axisLabel: {
            ...AXIS_CONFIG.TIME.BOTTOM,
            customValues: generateTimeAxisValues(minDate, maxDate, chartPreference.time_grid_side),
          },
          splitLine: AXIS_CONFIG.SPLIT_LINE,
        },
        {
          type: 'time',
          min: minDate,
          max: maxDate,
          position: 'top',
          axisTick: {
            customValues: generateTimeAxisValues(minDate, maxDate, chartPreference.time_grid_side),
          },
          axisLabel: {
            show: true,
            ...AXIS_CONFIG.TIME.TOP,
            customValues: generateTimeAxisValues(minDate, maxDate, chartPreference.time_grid_side),
          },
        },
      ],
      yAxis,
      animationDurationUpdate: 0,
      dataZoom,
      series,
    };

    return chartOptions;
  };

  const generateCycle = (cycleComponents: CycleComponents) => {
    const chartInstance = chartRef.current?.getEchartsInstance() ?? null;
    const dataZoom = chartInstance ? chartInstance.getOption().dataZoom : null;

    const chartOptions = getOptions({
      ...cycleComponents,
      currentDataZoom: dataZoom as DataZoomComponentOption[],
      dateRange,
      dateTo: dateToTimestamp,
    });

    return chartOptions;
  };
  /*========================Options===============================*/

  const checkChartZone = (event: any) => {
    const chartContainer = document.querySelector('.c-cycle-chart-preview__container') as HTMLElement;
    const chartElement = document.querySelector('.c-cycle-chart-preview__inner') as HTMLElement;

    const rect = chartContainer.getBoundingClientRect();
    const chartElementRect = chartElement.getBoundingClientRect();

    const topPart = window.getComputedStyle(chartContainer, '::before');
    const bottomPart = window.getComputedStyle(chartContainer, '::after');
    const leftPart = window.getComputedStyle(chartElement, '::before');
    const rightChartElement = window.getComputedStyle(chartElement, '::after');

    if (leftPart && event.offsetX <= CHART_INDENTS.GRID.LEFT) {
      return false;
    }

    if (rightChartElement && event.clientX >= rect.right - CHART_INDENTS.GRID.RIGHT) {
      return false;
    }

    if (topPart && event.offsetY <= CHART_INDENTS.GRID.TOP) {
      return false;
    }

    if (bottomPart && event.clientY >= chartElementRect.bottom - CHART_INDENTS.GRID.BOTTOM) {
      return false;
    }

    return true;
  };

  const areObjectsClose = (coordinateOne: Coordinate, coordinateTwo: Coordinate, tolerance: number = 1) =>
    Math.abs(coordinateOne.x - coordinateTwo.x) <= tolerance &&
    Math.abs(coordinateOne.y - coordinateTwo.y) <= tolerance;

  const viewCycleCreation = (event: any) => {
    const isClickZone = checkChartZone(event);

    const currentDataEvent = { ...dataEvent.current };
    dataEvent.current = null;

    if (!isClickZone) {
      return false;
    }

    const isCurrentPoint = areObjectsClose(
      {
        x: currentDataEvent.pointerdown.offsetX,
        y: currentDataEvent.pointerdown.offsetY,
      },
      {
        x: event.offsetX,
        y: event.offsetY,
      },
    );

    if (
      (currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_X ||
        currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_Y) &&
      !isCurrentPoint
    ) {
      return false;
    }

    if (Array.isArray(currentDataEvent.zoom) && !isCurrentPoint) {
      return false;
    }

    return true;
  };

  const updateDataEvent = (data: any, name: keyof DataEvent) => {
    dataEvent.current = { ...dataEvent.current, [name]: data };
  };

  const getOptionsData = () => {
    const currentOptions = generateCycle(cycleComponents);

    return currentOptions;
  };

  const onPointerDownHandler = useCallback((event) => {
    updateDataEvent(event, 'pointerdown');
  }, []);

  const onPointerMoveHandler = useCallback((event) => {
    updateDataEvent(event, 'pointermove');
  }, []);

  const onClickChartHandler = (event) => {
    const canCreate = viewCycleCreation(event);

    if (canCreate) {
      const instance = chartRef.current.getEchartsInstance();
      createNote(event, instance);
    }
  };

  useEffect(() => {
    if (chartReadyCounter) {
      const instance = chartRef?.current?.getEchartsInstance();
      const dom = instance.getDom();
      instance.on('click', editNote);
      dom.addEventListener('pointerdown', onPointerDownHandler);
      dom.addEventListener('pointermove', onPointerMoveHandler);
      dom.addEventListener('click', onClickChartHandler);
      return () => {
        dom.removeEventListener('pointerdown', onPointerDownHandler);
        dom.removeEventListener('pointermove', onPointerMoveHandler);
        dom.removeEventListener('click', onClickChartHandler);
      };
    }
  }, [chartReadyCounter]);

  return (
    <>
      <div className="c-cycle-chart-preview relative z-0 h-full">
        <div
          className={`c-cycle-chart-preview__container h-full flex-auto ${CLASS_NAME_SPACE.BOTTOM} ${CLASS_NAME_SPACE.TOP}`}
        >
          <div className={`c-cycle-chart-preview__inner h-full ${CLASS_NAME_SPACE.LEFT} ${CLASS_NAME_SPACE.RIGHT}`}>
            <ReactECharts
              onChartReady={onChartReady}
              ref={chartRef}
              option={getOptionsData()}
              style={{ height: '100%', width: '100%' }}
              className="c-cycle-chart-preview__chart echart"
              showLoading={isLoading}
              loadingOption={{
                text: t('labels.loading'),
                color: '#EF8541',
              }}
            />
          </div>
        </div>
      </div>

      {chartRef?.current && !!channelsToHide && (
        <ListLegendsCycle
          list={getSerIdWithColor()}
          cycleControllersName={cycleControllersName}
          nameDevices={nameDevices}
          channelsToHide={channelsToHide}
        />
      )}
    </>
  );
};
